import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/homeworks/HomeworksAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useHomeworkReportUIContext } from "./HomeworkReportUIContext";
import { HomeworkReportFilter } from "./homework-report-filter/HomeworkReportFilter";
import { HomeworkReportTable } from "./homework-report-table/HomeworkReportTable";

export function HomeworkReportCard() {
  // Homework Report Card UI Context
  const homeworkReportUIContext = useHomeworkReportUIContext();
  const homeworkReportUIProps = useMemo(() => {
    return {
      queryParams: homeworkReportUIContext.queryParams
    };
  }, [homeworkReportUIContext]);

  // Getting curret state of Homework Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.homeworks }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Homeworks Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Ödev Raporunu Excele Aktarır */ 
  const exportHomeworkReportToExcel = () => {
    dispatch(actions.getAllHomeworkReport(homeworkReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 25, 30, 25, 30, 40, 50];
      const columns = ["Öğretmen", "Sınıf", "Ders", "Toplam Ödev Sayısı", "Değerlendirilen Ödev Sayısı", "Değerlendirilmeyen Ödev Sayısı", "Değerlendirilme Tarihi Gelmeyen Ödev Sayısı"];
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.course,
        entity.homeworkCount,
        entity.evaluatedHomeworkCount,
        entity.unevaluatedHomeworkCount,
        entity.notYetDueHomeworkCount
      ]));
      const filename = homeworkReportUIProps.queryParams.term + " - " + (homeworkReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Raporu";
      const sheetname = "Ödev Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Ödev Raporunu PDF e Aktarır */
  const exportHomeworkReportToPDF = () => {
    dispatch(actions.getAllHomeworkReport(homeworkReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.course)
        },
        {
          header: "Toplam Ödev Sayısı",
          rows: entities.map(entity => entity.homeworkCount)
        },
        {
          header: "Değerlendirilen Ödev Sayısı",
          rows: entities.map(entity => entity.evaluatedHomeworkCount)
        },
        {
          header: "Değerlendirilmeyen Ödev Sayısı",
          rows: entities.map(entity => entity.unevaluatedHomeworkCount)
        },
        {
          header: "Değerlendirilme Tarihi Gelmeyen Ödev Sayısı",
          rows: entities.map(entity => entity.notYetDueHomeworkCount)
        }
      ];
      
      const filename = homeworkReportUIProps.queryParams.term + " - " + (homeworkReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Ödev Raporunu Yazdırma Penceresini Açar */
  const printHomeworkReport = () => {
    dispatch(actions.getAllHomeworkReport(homeworkReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      const header = homeworkReportUIProps.queryParams.term + " - " + (homeworkReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Raporu";
      const properties = ["Öğretmen", "Sınıf", "Ders", "Toplam Ödev Sayısı", "Değerlendirilen Ödev Sayısı", "Değerlendirilmeyen Ödev Sayısı", "Değerlendirilme Tarihi Gelmeyen Ödev Sayısı"];

      entities.forEach(entity => {
        data.push({          
          "Öğretmen": entity.teacher,
          "Sınıf": entity.section,
          "Ders": entity.course,
          "Toplam Ödev Sayısı": entity.homeworkCount,
          "Değerlendirilen Ödev Sayısı": entity.evaluatedHomeworkCount,
          "Değerlendirilmeyen Ödev Sayısı": entity.unevaluatedHomeworkCount,
          "Değerlendirilme Tarihi Gelmeyen Ödev Sayısı": entity.notYetDueHomeworkCount
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Ödev Raporunu Panoya Kopyalar */
  const copyHomeworkReport = () => {
    dispatch(actions.getAllHomeworkReport(homeworkReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Öğretmen", "Sınıf", "Ders", "Toplam Ödev Sayısı", "Değerlendirilen Ödev Sayısı", "Değerlendirilmeyen Ödev Sayısı", "Değerlendirilme Tarihi Gelmeyen Ödev Sayısı"];
      const header = homeworkReportUIProps.queryParams.term + " - " + (homeworkReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Raporu";
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.course,
        entity.homeworkCount,
        entity.evaluatedHomeworkCount,
        entity.unevaluatedHomeworkCount,
        entity.notYetDueHomeworkCount
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Ödev Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportHomeworkReportToExcel}
              exportToPDF={exportHomeworkReportToPDF}
              print={printHomeworkReport}
              copy={copyHomeworkReport}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <HomeworkReportFilter />
        <HomeworkReportTable />
      </CardBody>
    </Card>
  );
}